// „Verschieben“ wie in Close, aber mit Uhrzeit: Schnellwahlen mit Datum rechts (Morgen, nächste Wochentage, nächster
// Montag, 2 Wochen, 1 Monat, 3 Monate), darunter ein Monatskalender, darunter optional eine Uhrzeit (dann mit Erinnerung).
// Ein Klick auf eine Schnellwahl oder einen Tag wählt und schließt. Genutzt von der Zeile (Uhr-Symbol) und vom Auswahlmodus.
// Uhrzeit (19.10.2026, Simon): einfaches Textfeld statt Browser-Zeitfeld (das war bei halber Eingabe leer, die Uhrzeit
// „verschwand“ beim Klicken). Versteht 16, 1600, 16:00, 16.00; Enter übernimmt sie für den aktuellen Tag der Aufgabe.
import { useState } from "react";
import { berlinTeile, berlinZuIso, gueltig, heute } from "../../werkzeuge/zeit";
import { Ausklapp } from "../Ausklapp";
import { Icon } from "../Icon";
import { ZeitFeld } from "../ZeitFeld";
import { kalenderWochen, kurzDatum, monatName, monatVerschieben, schnellwahlen, WOCHENTAG_KURZ } from "./schnellwahl";
import "./inbox.css";

interface Props {
  anker: HTMLElement | null;
  offen: boolean;
  onSchliessen: () => void;
  /** Aktuelles Datum der Aufgabe (Datum oder ISO). Eine vorhandene Uhrzeit wird vorbelegt und bei Schnellwahlen mitgenommen. */
  aktuell?: string | null;
  /** Gewählt: YYYY-MM-DD, mit Uhrzeit ISO (Berlin -> UTC) */
  onWahl: (date: string) => void;
  ausrichtung?: "start" | "end";
  /** Hinweis unter dem Kalender, z. B. „Gilt für 12 Aufgaben“ */
  hinweis?: string;
}

export function VerschiebenPopover({ anker, offen, onSchliessen, aktuell, onWahl, ausrichtung = "end", hinweis }: Props) {
  return (
    <Ausklapp anker={anker} offen={offen} onSchliessen={onSchliessen} ausrichtung={ausrichtung} breite={272} minBreite={272} maxHoehe={620} rolle="dialog">
      {offen ? <Inhalt aktuell={aktuell ?? null} onWahl={onWahl} onSchliessen={onSchliessen} hinweis={hinweis} /> : null}
    </Ausklapp>
  );
}

function Inhalt({ aktuell, onWahl, onSchliessen, hinweis }: { aktuell: string | null; onWahl: (d: string) => void; onSchliessen: () => void; hinweis?: string }) {
  const h = heute();
  const aktuellTeile = aktuell && aktuell.length > 10 && gueltig(aktuell) ? berlinTeile(aktuell) : null;
  const aktuellerTag = aktuell ? (aktuellTeile ? aktuellTeile.datum : aktuell.slice(0, 10)) : null;
  const [zeit, setZeit] = useState(aktuellTeile?.zeit ?? "");
  const [monat, setMonat] = useState((aktuellerTag && aktuellerTag >= h ? aktuellerTag : h).slice(0, 7));

  const [zeitFehler, setZeitFehler] = useState(false);

  const wahl = (tag: string, z: string = zeit) => {
    if (zeitFehler) return; // ungültige Uhrzeit bleibt rot stehen, nichts wird gewählt
    onWahl(z ? berlinZuIso(tag, z) : tag);
    onSchliessen();
  };

  return (
    <div className="verschieben">
      <div className="verschieben__ueberschrift">Verschieben auf</div>
      <ul className="verschieben__liste">
        {schnellwahlen(h, aktuellerTag).map((s) => (
          <li key={s.label}>
            <button type="button" className="verschieben__punkt" onClick={() => wahl(s.tag)}>
              <span>{s.label}</span>
              <span className="verschieben__datum">{kurzDatum(s.tag)}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="kalender" aria-label="Kalender">
        <div className="kalender__kopf">
          <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Voriger Monat" onClick={() => setMonat((m) => monatVerschieben(m, -1))} disabled={monat <= h.slice(0, 7)}>
            <Icon name="pfeil-links" groesse={14} />
          </button>
          <span className="kalender__monat">{monatName(monat)}</span>
          <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Nächster Monat" onClick={() => setMonat((m) => monatVerschieben(m, 1))}>
            <Icon name="pfeil-rechts" groesse={14} />
          </button>
        </div>
        <div className="kalender__raster" role="grid">
          {[1, 2, 3, 4, 5, 6, 0].map((w) => (
            <span key={w} className="kalender__wochentag" role="columnheader">
              {WOCHENTAG_KURZ[w]}
            </span>
          ))}
          {kalenderWochen(monat).flat().map((t) => {
            const vorbei = t.tag < h;
            return (
              <button
                key={t.tag}
                type="button"
                role="gridcell"
                className={`kalender__tag${t.imMonat ? "" : " kalender__tag--fremd"}${t.tag === h ? " kalender__tag--heute" : ""}${t.tag === aktuellerTag ? " kalender__tag--aktuell" : ""}`}
                disabled={vorbei}
                aria-label={`${kurzDatum(t.tag)}${t.tag.slice(0, 4)}`}
                onClick={() => wahl(t.tag)}
              >
                {Number(t.tag.slice(8, 10))}
              </button>
            );
          })}
        </div>
      </div>
      <label className="verschieben__zeit">
        <span>Uhrzeit</span>
        <ZeitFeld
          className="eingabe eingabe--klein"
          wert={zeit}
          onChange={setZeit}
          onUngueltig={setZeitFehler}
          onEnter={(z) => z && wahl(aktuellerTag && aktuellerTag >= h ? aktuellerTag : h, z)}
          ariaLabel="Uhrzeit (optional, dann mit Erinnerung)"
          title="Optional, z. B. 16 oder 16:30. Enter übernimmt die Uhrzeit für den aktuellen Tag; sonst Tag anklicken."
        />
        {zeit ? (
          <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => setZeit("")}>
            Ohne
          </button>
        ) : (
          <span className="leise">Enter = heute bzw. Tag der Aufgabe</span>
        )}
      </label>
      {hinweis ? <div className="verschieben__hinweis">{hinweis}</div> : null}
    </div>
  );
}
